import axios from 'axios';
import { API_URL } from '../constants/api'; // Importar la constante

// Constantes del servicio
const STORAGE_KEYS = {
  USER: 'user',
  IS_AUTHENTICATED: 'isAuthenticated'
};

const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Error de conexión. Verifica tu conexión a internet.',
  LOGIN_ERROR: 'Error en el login',
  AUTH_CHECK_ERROR: 'Error verificando autenticación',
  STORAGE_ERROR: 'Error accediendo al almacenamiento local'
};

// Crear instancia de axios con configuración base
const api = axios.create({
  baseURL: API_URL, // Usar la constante centralizada
  timeout: 10000, // 10 segundos timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Servicio de autenticación
 * Maneja todas las operaciones relacionadas con la autenticación de usuarios
 */
class AuthService {
  
  /**
   * Normalizar email
   * @param {string} email - Email a normalizar
   * @returns {string} - Email normalizado
   * @private
   */
  #normalizeEmail(email) {
    return email?.trim().toLowerCase() || '';
  }

  /**
   * Validar email
   * @param {string} email - Email a validar
   * @returns {boolean} - True si es válido
   * @private
   */
  #isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validar contraseña
   * @param {string} password - Contraseña a validar
   * @returns {boolean} - True si es válida
   * @private
   */
  #isValidPassword(password) {
    return password && password.length >= 6;
  }

  /**
   * Guardar datos de usuario en localStorage de forma segura
   * @param {Object} userData - Datos del usuario
   * @private
   */
  #saveUserData(userData) {
    try {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
      localStorage.setItem(STORAGE_KEYS.IS_AUTHENTICATED, 'true');
    } catch (error) {
      console.error('Error guardando datos de usuario:', error);
      throw new Error(ERROR_MESSAGES.STORAGE_ERROR);
    }
  }

  /**
   * Limpiar datos de usuario del localStorage
   * @private
   */
  #clearUserData() {
    try {
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.IS_AUTHENTICATED);
    } catch (error) {
      console.error('Error limpiando datos de usuario:', error);
    }
  }

  /**
   * Iniciar sesión
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña del usuario
   * @returns {Promise<Object>} - Promesa con la respuesta del servidor
   * @throws {Error} - Error de validación, red o servidor
   */
  async login(email, password) {
    // Validaciones de entrada
    if (!email || !password) {
      throw new Error('Email y contraseña son requeridos');
    }

    const normalizedEmail = this.#normalizeEmail(email);
    
    if (!this.#isValidEmail(normalizedEmail)) {
      throw new Error('Email no válido');
    }

    if (!this.#isValidPassword(password)) {
      throw new Error('La contraseña debe tener al menos 6 caracteres');
    }

    try {
      const response = await api.post('/auth/login', { // Quitar /api
        email: normalizedEmail,
        password: password
      });
      
      // Verificar respuesta exitosa - Manejo de nueva estructura del backend refactorizado
      if (response.data?.success && response.data?.data?.user) {
        // Mapear la respuesta del backend a una estructura de usuario consistente para el frontend
        const backendUser = response.data.data.user; // Cambio: ahora está en data.user
        const frontendUser = {
          id: backendUser.user_id,
          name: backendUser.nombre,
          email: backendUser.correo,
          role: backendUser.role || backendUser.rol, // Manejar ambos formatos
          celular: backendUser.celular,
          createdAt: backendUser.created_at,
          token: response.data.data.token, // El token también puede estar en data
        };

        this.#saveUserData(frontendUser);
        return { success: true, user: frontendUser, message: response.data.message };
      }
      
      // Respuesta no exitosa
      throw new Error(response.data?.message || 'Credenciales incorrectas');
      
    } catch (error) {
      console.error('Error en login:', error);
      
      // Si es un error de validación que lanzamos, re-lanzarlo
      if (error.message.includes('Email') || 
          error.message.includes('contraseña') || 
          error.message.includes('Credenciales')) {
        throw error;
      }
      
      // Manejar errores de respuesta del servidor con nueva estructura
      if (error.response?.data) {
        // Si hay estructura nueva del backend con success: false
        if (error.response.data.success === false) {
          throw new Error(error.response.data.error || error.response.data.message || ERROR_MESSAGES.LOGIN_ERROR);
        }
        // Manejo legacy
        throw new Error(error.response.data.message || ERROR_MESSAGES.LOGIN_ERROR);
      }
      
      // Manejar errores de red o timeout
      if (error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR') {
        throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
      }
      
      // Error genérico
      throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
    }
  }

  /**
   * Cerrar sesión
   * Limpia todos los datos de autenticación del localStorage
   */
  logout() {
    this.#clearUserData();
  }

  /**
   * Obtener usuario actual desde localStorage
   * @returns {Object|null} - Datos del usuario o null si no está autenticado
   */
  getCurrentUser() {
    try {
      const userString = localStorage.getItem(STORAGE_KEYS.USER);
      
      if (!userString) {
        return null;
      }
      
      const user = JSON.parse(userString);
      
      // Validar que el objeto de usuario tenga las propiedades mínimas (formato nuevo o antiguo)
      const isValidNewFormat = user && typeof user === 'object' && user.id && user.email;
      const isValidOldFormat = user && typeof user === 'object' && user.user_id && user.correo;

      if (!isValidNewFormat && !isValidOldFormat) {
        console.warn('Datos de usuario inválidos en localStorage');
        this.#clearUserData();
        return null;
      }
      
      // Si está en formato antiguo, convertirlo al nuevo formato
      if (isValidOldFormat && !isValidNewFormat) {
        return {
          id: user.user_id,
          name: user.nombre,
          email: user.correo,
          role: user.rol,
          celular: user.celular,
          createdAt: user.created_at
        };
      }
      
      return user; // Devuelve el usuario en el formato nuevo/estándar
    } catch (error) {
      console.error('Error obteniendo usuario actual:', error);
      this.#clearUserData(); // Limpiar datos corruptos
      return null;
    }
  }

  /**
   * Verificar si el usuario está autenticado
   * @returns {boolean} - True si está autenticado, false si no
   */
  isAuthenticated() {
    try {
      const isAuth = localStorage.getItem(STORAGE_KEYS.IS_AUTHENTICATED);
      const user = this.getCurrentUser();
      
      return isAuth === 'true' && user !== null;
    } catch (error) {
      console.error('Error verificando autenticación:', error);
      return false;
    }
  }

  /**
   * Verificar estado de autenticación con el servidor
   * @returns {Promise<Object>} - Promesa con la verificación del servidor
   * @throws {Error} - Error de red o servidor
   */
  async checkAuth() {
    try {
      const response = await api.get('/auth/check'); // Quitar /api
      return response.data;
    } catch (error) {
      console.error('Error verificando autenticación con servidor:', error);
      
      // Si hay error 401 o 403, limpiar datos locales
      if (error.response?.status === 401 || error.response?.status === 403) {
        this.#clearUserData();
      }
      
      throw new Error(ERROR_MESSAGES.AUTH_CHECK_ERROR);
    }
  }

  /**
   * Obtener el token de autenticación del usuario actual
   * @returns {string|null} - El token o null si no existe
   */
  getAuthToken() {
    try {
      const userString = localStorage.getItem(STORAGE_KEYS.USER);
      if (userString) {
        const user = JSON.parse(userString);
        return user?.token || null;
      }
      return null;
    } catch (error) {
      console.error('Error obteniendo token:', error);
      return null;
    }
  }

  /**
   * Refrescar datos del usuario actual desde el servidor
   * @returns {Promise<Object>} - Promesa con los datos actualizados del usuario
   * @throws {Error} - Error de red o servidor
   */
  async refreshCurrentUser() {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser || !currentUser.email) {
        throw new Error('No hay usuario autenticado');
      }

      const response = await api.get(`/users/profile/${currentUser.email}`); // Esta ruta no lleva /api, ya es correcta
      
      if (response.data?.success && response.data?.user) {
        // Mapear rol -> role para consistencia con permissionUtils
        const userData = {
          ...response.data.user,
          role: response.data.user.rol // Mapear rol del backend a role para frontend
        };
        this.#saveUserData(userData);
        return userData;
      }
      
      throw new Error(response.data?.message || 'Error al obtener datos del usuario');
      
    } catch (error) {
      console.error('Error refrescando usuario:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        this.#clearUserData();
        throw new Error('Sesión expirada');
      }
      
      throw new Error(error.response?.data?.message || 'Error al refrescar usuario');
    }
  }

  /**
   * Obtener información de la API (para debugging)
   * @returns {Object} - Información de configuración
   */
  getApiInfo() {
    return {
      url: API_URL,
      storageKeys: STORAGE_KEYS
    };
  }
}

const authServiceInstance = new AuthService();

export const getAuthToken = () => {
  return authServiceInstance.getAuthToken();
};

export default authServiceInstance; 